/**
 * The wire shape between the sign-in popup and the panel that opened it (ADR-050 D3).
 *
 * Deliberately its own module, imported by BOTH halves, because the two run in different documents and a drift
 * between them fails silently: the opener would simply never see a message it recognises, and the panel would sit
 * in its not-signed-in state with nothing in the console.
 *
 * **This is NOT the Unity bridge.** ADR-049's listener allow-lists the UNITY origin and must never learn to accept
 * a credential (ADR-050 D1); this one allow-lists our OWN origin and carries an authorization code. Two listeners,
 * two allow-lists, no overlap — keeping the shapes in separate modules is part of keeping that true.
 */

/** Discriminator. Namespaced because `window.message` is a shared bus — extensions and libraries post here too. */
export const WEBGL_AUTH_MESSAGE = "arc:webgl-auth";

/**
 * What the popup hands back.
 *
 * It carries an authorization CODE, never a token: the code is single-use and worthless without the PKCE verifier,
 * which never leaves the opener's memory (D3). `state` is always present — on the success and the failure path
 * alike — because the opener matches it before acting on either.
 */
export interface WebglAuthMessage {
  type: typeof WEBGL_AUTH_MESSAGE;
  /** Echo of the opener's one-time state. Checked against the in-memory value before anything else happens. */
  state: string;
  /** Present on success. */
  code?: string;
  /** Present when Keycloak refused (`access_denied`, `login_required`, `authentication_expired`, …). */
  error?: string;
  /** Keycloak's human-readable detail. Surfaced to the user, never used for control flow. */
  errorDescription?: string;
}

/**
 * Structural check on a value that arrived from `postMessage`.
 *
 * Validates the SHAPE only. It is not a trust decision: the origin and `event.source` checks in
 * `webgl-popup-auth.ts` are what establish who sent it, and this runs after both. Kept strict anyway — a message
 * whose `state` is not a string would otherwise reach the comparison as `undefined === undefined` on a malformed
 * pair and pass.
 */
export function isWebglAuthMessage(v: unknown): v is WebglAuthMessage {
  if (typeof v !== "object" || v === null) return false;
  const m = v as Partial<WebglAuthMessage>;
  if (m.type !== WEBGL_AUTH_MESSAGE) return false;
  if (typeof m.state !== "string" || m.state === "") return false;
  if (m.code !== undefined && typeof m.code !== "string") return false;
  if (m.error !== undefined && typeof m.error !== "string") return false;
  return true;
}
